/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import urlJoin from 'proper-url-join';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  isCpd,
  endpoints,
  isWxoEnabled
} from '@ibm-watson/shared/modules/ia/util/config';
import { pageEvents } from '../../util/trackers/segment';
import { getUrlParameter, getPath } from '../../util/url';

const Login: React.FC = () => {
  const { instance } = useParams<{ instance: string }>();

  let successRedirect: string;

  // If the redirect query parameter is specified, use it to determine where
  // the user should be redirected after login; if not specified, generate a
  // generic URL that incorporates the instance, if provided.
  if (getUrlParameter('redirect')) {
    successRedirect = getUrlParameter('redirect');
  } else {
    const basePath = getPath(document.baseURI);
    if (instance) {
      successRedirect = urlJoin(basePath, instance, 'home');
    } else {
      successRedirect = urlJoin(basePath, 'instances');
    }
  }

  const authRedirect = urlJoin(endpoints.authenticate, 'bluemix', {
    query: {
      successRedirect,
      // Since the instance parameter is already URI-encoded, and it will
      // be encoded once again by urlJoin(), we need to decode it first.
      instance: instance ? decodeURIComponent(instance) : undefined
    }
  });

  useEffect(() => {
    pageEvents.login();
  }, []);

  // CP4D users do not authenticate the same way, and will in fact always be
  // authenticated by the time Tooling is loaded, so directly redirect them
  // to the appropriate page.
  // Likewise, in the WXO environment Tooling will always be authenticated with
  // the watsonx Orchestrate JWT.
  // In the Cloud environment, immediately take the user to the authorization
  // endpoint, which will either automatically redirect an unauthenticated user
  // to the IAM login page, or an authenticated user to the correct page in
  // Tooling.
  if (isCpd || isWxoEnabled) {
    window.location.assign(successRedirect);
  } else {
    window.location.assign(authRedirect);
  }

  return null;
};

export default Login;
